import {AxiosError} from "axios";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {AntennaSignal as SMSIcon, Bin as DeleteIcon, Plus as SendSMSIcon} from "iconoir-react";
import {ComponentType, useCallback, useEffect, useState} from "react";
import {Dropdown} from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useNavigate} from "react-router-dom";

/**
 * A single SMS message shown in the queue. Matches the SMSMessage shape returned by the back-end.
 */
export interface SMSQueueMessage {
    id: string;
    phoneNumber: string;
    sender?: string;
    message: string;
    created: number;
    lastTry: number;
    lastError: string;
}

/**
 * The SMS back-end operations used by the SMS queue. Provided by the consuming app so the queue can be
 * reused with different back-ends (admin vs company portal). Methods resolve to the data directly.
 */
export interface SMSQueueOperations {
    getSMSQueueSize(): Promise<number>;
    getSMSMessages(firstResult: number, maxResults: number): Promise<SMSQueueMessage[]>;
    deleteSMS(ids: string[]): Promise<void>;
}

interface SMSQueueProps {
    /**
     * The SMS back-end operations. Must be a stable reference (e.g. defined at module scope) to avoid
     * reload loops.
     */
    operations: SMSQueueOperations;
    /**
     * The dialog used to send a new SMS. Provided by the consuming app so it can supply its own dialog.
     * The optional initialEmail is passed through to the dialog so it can pre-fill a recipient.
     */
    sendDialog: ComponentType<{ onClose: (closeCause: DialogCloseCause) => void, initialEmail?: string }>;
    /**
     * If true, the send SMS dialog is opened automatically when the queue is shown (e.g. via a deep link).
     */
    autoOpenSend?: boolean;
    /**
     * The email address to pre-fill in the send SMS dialog when it is auto-opened.
     */
    initialSendEmail?: string;
    /**
     * Called when the auto-opened send SMS dialog is closed. Lets the consuming app clean up the URL
     * (e.g. navigate away from the deep link back to the plain SMS queue).
     */
    onSendDialogClose?: () => void;
}

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
}

const Column = ({value, children, className, maxLength = 60}: ColumnProps) => {
    return (
        <td className={"user-select-all " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const SMSQueue = ({operations, sendDialog: SendDialog, autoOpenSend, initialSendEmail, onSendDialogClose}: SMSQueueProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The SMS messages to show in the table. The messages are fetched from the back-end.
     */
    const [messages, setMessages] = useState<SMSQueueMessage[]>([]);
    /**
     * The total number of SMS messages in the queue. The number is fetched from the back-end.
     */
    const [queueSize, setQueueSize] = useState<number>(0);
    /**
     * The error message if some general error occurred. The error is fetched from the back-end.
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected messages. The user selects the messages. The user can select multiple messages.
     */
    const [selectedMessages, setSelectedMessages] = useState<SMSQueueMessage[]>([]);
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadCounter, setReloadCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReload = useCallback(() => setReloadCounter(counter => counter + 1), []);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "send SMS" modal popup will be shown. Opened automatically when autoOpenSend is set
     * (e.g. when the page is reached via a deep link).
     */
    const [showSendSMSPopup, setShowSendSMSPopup] = useState<boolean>(autoOpenSend ?? false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        logger.debug("Load queue size")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        operations.getSMSQueueSize()
            .then(size => {
                if (superseded) {
                    return;
                }

                setQueueSize(size);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue size", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });

        return () => {
            superseded = true;
        }
    }, [navigate,
        operations,
        reloadCounter,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load messages")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        operations.getSMSMessages((currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(messages => {
                if (superseded) {
                    return;
                }

                setMessages(messages);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting messages", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        operations,
        reloadCounter,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const onChangeSelect = (checked : boolean, message : SMSQueueMessage) =>
    {
        setSelectedMessages(checked ?
            [...selectedMessages, message] :
            selectedMessages.filter(q => q.id !== message.id)
        );
    }

    const invertSelection = () => {
        setSelectedMessages([...messages].filter(q => !selectedMessages.includes(q)));
    }

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        operations.deleteSMS(selectedMessages.map(m => m.id))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedMessages([]);
            })
    }

    /**
     * Is called when the SMS Send Message Dalog is closed
     */
    const onShowSendSMSPopupDalogClose = () =>
    {
        setShowSendSMSPopup(false);
        forceReload();
        onSendDialogClose?.();
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("sms.are-you-sure-delete-sms")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showSendSMSPopup && <SendDialog onClose={onShowSendSMSPopupDalogClose} initialEmail={initialSendEmail} />}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<SMSIcon/>} title={t("sms.title")} onClickReload={forceReload}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => setShowSendSMSPopup(true)}>
                                        <SendSMSIcon className="text-success"/> {t("sms.send-sms")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>
                </div>

                <PageContent className="flex-grow-1 min-h-0">
                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x" bordered hover>
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    {selectedMessages.length === 0 ?
                                        <>
                                            <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                            <th>{t("sms.id")}</th>
                                            <th>{t("sms.phone-number")}</th>
                                            <th>{t("sms.sender")}</th>
                                            <th>{t("sms.created")}</th>
                                            <th>{t("sms.last-try")}</th>
                                            <th>{t("sms.last-error")}</th>
                                        </>
                                        :
                                        <th colSpan={6}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedMessages([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedMessages.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("sms.delete-selected")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    }
                                </tr>
                                </thead>
                                <tbody>
                                {messages?.map((m: SMSQueueMessage) => (
                                    <tr key={m.id}>
                                        <Column className="td-w-min">
                                            <Form.Check
                                                type="checkbox"
                                                className="me-0"
                                                checked={selectedMessages.includes(m)}
                                                onChange={(e) => onChangeSelect(e.target.checked, m)}
                                                inline
                                            />
                                        </Column>
                                        <Column value={m.id}/>
                                        <Column value={m.phoneNumber}/>
                                        <Column value={m.sender}/>
                                        <Column value={DateUtils.fromTimestampToLocaleDateStringShort(m.created)}/>
                                        <Column value={DateUtils.fromTimestampToLocaleDateStringShort(m.lastTry)}/>
                                        <Column value={m.lastError} maxLength={100}/>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={queueSize ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>
        </>
    );
}